import {
  DecodingError,
  EncodingError,
  InvalidMessageError,
  MessageSizeError,
} from './errors/index.js';
import { CONTROL_CHARS, stuff, unstuff } from './internal/byte-stuffing.js';
import { getLogger, hexDump } from './internal/logger.js';
import type { ProtocolOptions } from './types/config.js';
import { DEFAULT_PROTOCOL_OPTIONS } from './types/config.js';
import type { EventMsgConfig, MessageHeader } from './types/index.js';

/**
 * Decoded message structure
 */
export interface DecodedMessage {
  header: MessageHeader;
  eventName: string;
  eventData: string;
}

/**
 * EventMsgV3 Protocol Implementation
 *
 * Handles encoding and decoding of EventMsgV3 binary protocol messages.
 * Message format: [SOH][StuffedHeader][STX][StuffedEventName][US][StuffedEventData][EOT]
 */
export class Protocol {
  private readonly options: Required<ProtocolOptions>;
  private readonly encoding: 'utf8' | 'binary';
  private readonly logger: ReturnType<typeof getLogger>;

  constructor(config: EventMsgConfig) {
    this.options = { ...DEFAULT_PROTOCOL_OPTIONS, ...config.protocol };
    this.encoding = config.encoding ?? 'utf8';
    this.logger = getLogger('PROTOCOL');
  }

  /**
   * Encode a message into EventMsgV3 binary format
   *
   * @param header Message header (7 bytes)
   * @param eventName Event name string
   * @param eventData Event data string (JSON)
   * @returns Encoded binary message
   * @throws {EncodingError} If encoding fails
   * @throws {MessageSizeError} If message exceeds size limits
   */
  encode(
    header: MessageHeader,
    eventName: string,
    eventData: string
  ): Uint8Array {
    this.logger.debug('Starting message encode', {
      eventName,
      eventDataLength: eventData.length,
      header,
      encoding: this.encoding,
    });

    try {
      // Validate inputs
      this.validateHeader(header);
      this.validateEventName(eventName);
      this.validateEventData(eventData);

      // Convert strings to bytes
      const eventNameBytes = this.stringToBytes(eventName);
      const eventDataBytes = this.stringToBytes(eventData);

      // Serialize header (7 bytes, big-endian messageId)
      const headerBytes = this.serializeHeader(header);

      // Apply byte stuffing to each section
      const stuffedHeader = stuff(headerBytes);
      const stuffedEventName = stuff(eventNameBytes);
      const stuffedEventData = stuff(eventDataBytes);

      this.logger.trace('Byte stuffing complete', {
        headerSize: `${headerBytes.length} -> ${stuffedHeader.length}`,
        nameSize: `${eventNameBytes.length} -> ${stuffedEventName.length}`,
        dataSize: `${eventDataBytes.length} -> ${stuffedEventData.length}`,
      });

      // Calculate total message size
      const totalSize =
        1 +
        stuffedHeader.length +
        1 +
        stuffedEventName.length +
        1 +
        stuffedEventData.length +
        1;

      if (totalSize > this.options.maxEventDataLength + 1000) {
        // Allow some overhead
        throw new MessageSizeError(
          totalSize,
          this.options.maxEventDataLength + 1000,
          'encoded message'
        );
      }

      // Build final message: [SOH][StuffedHeader][STX][StuffedEventName][US][StuffedEventData][EOT]
      const message = new Uint8Array(totalSize);
      let offset = 0;

      // SOH
      message[offset++] = CONTROL_CHARS.SOH;

      // Stuffed Header
      message.set(stuffedHeader, offset);
      offset += stuffedHeader.length;

      // STX
      message[offset++] = CONTROL_CHARS.STX;

      // Stuffed Event Name
      message.set(stuffedEventName, offset);
      offset += stuffedEventName.length;

      // US
      message[offset++] = CONTROL_CHARS.US;

      // Stuffed Event Data
      message.set(stuffedEventData, offset);
      offset += stuffedEventData.length;

      // EOT
      message[offset++] = CONTROL_CHARS.EOT;

      const finalMessage = message.subarray(0, offset);

      this.logger.info('Message encoded successfully', {
        eventName,
        finalSize: finalMessage.length,
        messageId: header.messageId,
      });

      this.logger.trace('Encoded message hex dump', {
        hex: hexDump(finalMessage),
      });

      return finalMessage;
    } catch (error) {
      this.logger.error('Message encoding failed', {
        error: error instanceof Error ? error.message : String(error),
        eventName,
        eventDataLength: eventData.length,
        cause: error instanceof Error ? error.name : 'Unknown',
      });

      if (error instanceof MessageSizeError) {
        throw error;
      }
      throw new EncodingError('Failed to encode EventMsgV3 message', {
        context: { eventName, eventDataLength: eventData.length },
        cause: error instanceof Error ? error : new Error(String(error)),
      });
    }
  }

  /**
   * Encode a message with binary data into EventMsgV3 format
   *
   * @param header Message header (7 bytes)
   * @param eventName Event name string
   * @param eventData Binary event data
   * @returns Encoded binary message
   * @throws {EncodingError} If encoding fails
   * @throws {MessageSizeError} If message exceeds size limits
   */
  encodeBinary(
    header: MessageHeader,
    eventName: string,
    eventData: Uint8Array
  ): Uint8Array {
    this.logger.debug('Starting binary message encode', {
      eventName,
      eventDataLength: eventData.length,
      header,
    });

    try {
      // Validate inputs
      this.validateHeader(header);
      this.validateEventName(eventName);
      
      // Validate binary data size
      if (eventData.length > this.options.maxEventDataLength) {
        throw new MessageSizeError(
          eventData.length,
          this.options.maxEventDataLength,
          'eventData'
        );
      }

      // Convert event name to bytes
      const eventNameBytes = this.stringToBytes(eventName);

      // Serialize header (7 bytes, big-endian messageId)
      const headerBytes = this.serializeHeader(header);

      // Apply byte stuffing to each section
      const stuffedHeader = stuff(headerBytes);
      const stuffedEventName = stuff(eventNameBytes);
      const stuffedEventData = stuff(eventData);

      this.logger.trace('Binary byte stuffing complete', {
        headerSize: `${headerBytes.length} -> ${stuffedHeader.length}`,
        nameSize: `${eventNameBytes.length} -> ${stuffedEventName.length}`,
        dataSize: `${eventData.length} -> ${stuffedEventData.length}`,
      });

      // Calculate total message size
      const totalSize =
        1 +
        stuffedHeader.length +
        1 +
        stuffedEventName.length +
        1 +
        stuffedEventData.length +
        1;

      if (totalSize > this.options.maxEventDataLength + 1000) {
        // Allow some overhead
        throw new MessageSizeError(
          totalSize,
          this.options.maxEventDataLength + 1000,
          'encoded message'
        );
      }

      // Build final message: [SOH][StuffedHeader][STX][StuffedEventName][US][StuffedEventData][EOT]
      const message = new Uint8Array(totalSize);
      let offset = 0;

      // SOH
      message[offset++] = CONTROL_CHARS.SOH;

      // Stuffed Header
      message.set(stuffedHeader, offset);
      offset += stuffedHeader.length;

      // STX
      message[offset++] = CONTROL_CHARS.STX;

      // Stuffed Event Name
      message.set(stuffedEventName, offset);
      offset += stuffedEventName.length;

      // US
      message[offset++] = CONTROL_CHARS.US;

      // Stuffed Event Data (binary)
      message.set(stuffedEventData, offset);
      offset += stuffedEventData.length;

      // EOT
      message[offset++] = CONTROL_CHARS.EOT;

      const finalMessage = message.subarray(0, offset);

      this.logger.info('Binary message encoded successfully', {
        eventName,
        finalSize: finalMessage.length,
        messageId: header.messageId,
      });

      this.logger.trace('Encoded binary message hex dump', {
        hex: hexDump(finalMessage),
      });

      return finalMessage;
    } catch (error) {
      this.logger.error('Binary message encoding failed', {
        error: error instanceof Error ? error.message : String(error),
        eventName,
        eventDataLength: eventData.length,
        cause: error instanceof Error ? error.name : 'Unknown',
      });

      if (error instanceof MessageSizeError) {
        throw error;
      }
      throw new EncodingError('Failed to encode EventMsgV3 binary message', {
        context: { eventName, eventDataLength: eventData.length },
        cause: error instanceof Error ? error : new Error(String(error)),
      });
    }
  }

  /**
   * Decode a binary message into EventMsgV3 components
   *
   * @param data Binary message data
   * @returns Decoded message components
   * @throws {DecodingError} If decoding fails
   * @throws {InvalidMessageError} If message format is invalid
   */
  decode(data: Uint8Array): DecodedMessage {
    this.logger.debug('Starting message decode', {
      messageLength: data.length,
      encoding: this.encoding,
    });

    this.logger.trace('Incoming message hex dump', {
      hex: hexDump(data),
    });

    try {
      // Validate minimum message length (SOH + header + STX + name + US + data + EOT)
      if (data.length < 10) {
        throw new InvalidMessageError(
          `Message too short: ${data.length} bytes (minimum 10)`,
          { context: { messageLength: data.length } }
        );
      }

      // Validate framing characters
      if (data[0] !== CONTROL_CHARS.SOH) {
        throw new InvalidMessageError(
          `Message must start with SOH (0x01), got 0x${data[0]?.toString(16).padStart(2, '0')}`,
          { context: { firstByte: data[0] } }
        );
      }

      if (data.at(-1) !== CONTROL_CHARS.EOT) {
        throw new InvalidMessageError(
          `Message must end with EOT (0x04), got 0x${data.at(-1)?.toString(16).padStart(2, '0')}`,
          { context: { lastByte: data.at(-1) } }
        );
      }

      // Find section boundaries
      const stxIndex = this.findControlChar(data, CONTROL_CHARS.STX, 1);
      const usIndex = this.findControlChar(
        data,
        CONTROL_CHARS.US,
        stxIndex + 1
      );

      if (stxIndex === -1) {
        throw new InvalidMessageError('STX (0x02) not found in message');
      }
      if (usIndex === -1) {
        throw new InvalidMessageError('US (0x1F) not found in message');
      }

      // Extract and unstuff sections
      const stuffedHeader = data.subarray(1, stxIndex);
      const stuffedEventName = data.subarray(stxIndex + 1, usIndex);
      const stuffedEventData = data.subarray(usIndex + 1, data.length - 1);

      const headerBytes = unstuff(stuffedHeader);
      const eventNameBytes = unstuff(stuffedEventName);
      const eventDataBytes = unstuff(stuffedEventData);

      this.logger.trace('Byte unstuffing complete', {
        headerSize: `${stuffedHeader.length} -> ${headerBytes.length}`,
        nameSize: `${stuffedEventName.length} -> ${eventNameBytes.length}`,
        dataSize: `${stuffedEventData.length} -> ${eventDataBytes.length}`,
      });

      // Parse components
      const header = this.deserializeHeader(headerBytes);
      const eventName = this.bytesToString(eventNameBytes);
      const eventData = this.bytesToString(eventDataBytes);

      // Validate decoded components
      if (this.options.strictValidation) {
        this.validateHeader(header);
        this.validateEventName(eventName);
        this.validateEventData(eventData);
      }

      this.logger.info('Message decoded successfully', {
        eventName,
        messageId: header.messageId,
        senderId: header.senderId,
        receiverId: header.receiverId,
        eventDataLength: eventData.length,
      });

      return { header, eventName, eventData };
    } catch (error) {
      this.logger.error('Message decoding failed', {
        error: error instanceof Error ? error.message : String(error),
        messageLength: data.length,

        cause: error instanceof Error ? error.name : 'Unknown',
        hex: hexDump(data, 64),
      });

      if (error instanceof InvalidMessageError) {
        throw error;
      }
      throw new DecodingError('Failed to decode EventMsgV3 message', {
        context: { messageLength: data.length },
        cause: error instanceof Error ? error : new Error(String(error)),
      });
    }
  }

  /**
   * Find a control character in data, skipping escaped instances
   */
  private findControlChar(
    data: Uint8Array,
    target: number,
    startIndex: number
  ): number {
    let escaped = false;

    for (let i = startIndex; i < data.length; i++) {
      const byte = data[i]!;

      if (escaped) {
        escaped = false;
      } else if (byte === CONTROL_CHARS.ESC) {
        escaped = true;
      } else if (byte === target) {
        return i;
      }
    }

    return -1;
  }

  /**
   * Serialize message header to 7 bytes (big-endian messageId)
   */
  private serializeHeader(header: MessageHeader): Uint8Array {
    const bytes = new Uint8Array(7);
    bytes[0] = header.senderId;
    bytes[1] = header.receiverId;
    bytes[2] = header.senderGroupId;
    bytes[3] = header.receiverGroupId;
    bytes[4] = header.flags;
    bytes[5] = (header.messageId >> 8) & 0xff; // MSB
    bytes[6] = header.messageId & 0xff; // LSB
    return bytes;
  }

  /**
   * Deserialize 7 bytes to message header (big-endian messageId)
   */
  private deserializeHeader(bytes: Uint8Array): MessageHeader {
    if (bytes.length !== 7) {
      throw new InvalidMessageError(
        `Header must be exactly 7 bytes, got ${bytes.length}`,
        { context: { headerLength: bytes.length } }
      );
    }

    return {
      senderId: bytes[0]!,
      receiverId: bytes[1]!,
      senderGroupId: bytes[2]!,
      receiverGroupId: bytes[3]!,
      flags: bytes[4]!,
      messageId: (bytes[5]! << 8) | bytes[6]!, // Big-endian
    };
  }

  /**
   * Convert string to bytes using configured encoding
   */
  private stringToBytes(str: string): Uint8Array {
    if (this.encoding === 'utf8') {
      return new TextEncoder().encode(str);
    }
    // Binary encoding - each character is a byte
    const bytes = new Uint8Array(str.length);
    for (let i = 0; i < str.length; i++) {
      bytes[i] = str.charCodeAt(i) & 0xff;
    }
    return bytes;
  }

  /**
   * Convert bytes to string using configured encoding
   */
  private bytesToString(bytes: Uint8Array): string {
    if (this.encoding === 'utf8') {
      return new TextDecoder('utf8').decode(bytes);
    }
    // Binary encoding - each byte is a character
    return String.fromCharCode(...bytes);
  }

  /**
   * Validate message header fields
   */
  private validateHeader(header: MessageHeader): void {
    const fields = [
      { name: 'senderId', value: header.senderId },
      { name: 'receiverId', value: header.receiverId },
      { name: 'senderGroupId', value: header.senderGroupId },
      { name: 'receiverGroupId', value: header.receiverGroupId },
      { name: 'flags', value: header.flags },
    ];

    for (const field of fields) {
      if (
        field.value < 0 ||
        field.value > 255 ||
        !Number.isInteger(field.value)
      ) {
        throw new MessageSizeError(field.value, 255, field.name, {
          context: { validRange: '0-255' },
        });
      }
    }

    if (
      header.messageId < 0 ||
      header.messageId > 65_535 ||
      !Number.isInteger(header.messageId)
    ) {
      throw new MessageSizeError(header.messageId, 65_535, 'messageId', {
        context: { validRange: '0-65535' },
      });
    }
  }

  /**
   * Validate event name length
   */
  private validateEventName(eventName: string): void {
    const bytes = this.stringToBytes(eventName);
    if (bytes.length > this.options.maxEventNameLength) {
      throw new MessageSizeError(
        bytes.length,
        this.options.maxEventNameLength,
        'eventName'
      );
    }
  }

  /**
   * Validate event data length
   */
  private validateEventData(eventData: string): void {
    const bytes = this.stringToBytes(eventData);
    if (bytes.length > this.options.maxEventDataLength) {
      throw new MessageSizeError(
        bytes.length,
        this.options.maxEventDataLength,
        'eventData'
      );
    }
  }
}
